<template>
  <div class="bp-card h-100">
    <div class="bp-card-header">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <div class="bp-card-title-sm">Tool Store</div>
          <div class="bp-card-caption">
            Browse tool cards by the current category and add them to the pipeline after configuration.
          </div>
        </div>
        <span class="badge bg-light text-muted border">
          {{ toolkitMergeList.length }} Total
        </span>
      </div>

      <!-- Search box -->
      <div class="mt-2 position-relative">
        <input
          v-model="keyword"
          type="text"
          class="form-control form-control-sm store-search-input"
          placeholder="Search by tool name, keywords, or tags…"
        />
        <button
          v-if="keyword"
          type="button"
          class="btn btn-sm btn-link text-muted position-absolute end-0 top-50 translate-middle-y me-2 p-0"
          @click="keyword = ''"
        >
          ×
        </button>
      </div>
    </div>

    <!-- Card grid -->
    <div class="store-grid-wrapper">
      <div v-if="filteredTools.length === 0" class="bp-empty">
        <p class="mb-1">No matching tools found</p>
        <p class="bp-card-caption mb-0">
          Try clearing the search or switching the category on the left.
        </p>
      </div>
      <div v-else class="row g-2">
        <div
          v-for="tool in filteredTools"
          :key="tool.software_name"
          class="col-12"
        >
          <div class="tool-card">
            <div
              class="tool-card-body"
              @click="openConfig(tool)"
            >
              <div class="tool-card-title-row">
                <div class="tool-card-title">{{ tool.display_name }}</div>
                <span class="tool-card-chip">
                  {{ tool.software_name }}
                </span>
              </div>
              <p v-if="tool.desc" class="tool-card-desc mb-1">
                {{ tool.desc }}
              </p>
              <div v-if="tool.tags && tool.tags.length" class="tool-card-tags">
                <span
                  v-for="tag in tool.tags"
                  :key="tag"
                  class="tool-tag"
                >
                  {{ tag }}
                </span>
              </div>
            </div>
            <div class="tool-card-footer">
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                @click.stop="openConfig(tool)"
              >
                Configure & Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tool configuration modal -->
    <div
      v-if="showConfig && currentTool"
      class="tool-config-backdrop d-flex justify-content-center align-items-center"
    >
      <div class="tool-config-modal">
        <div class="tool-config-header d-flex justify-content-between align-items-center">
          <div>
            <div class="tool-config-title">{{ currentTool.display_name }}</div>
            <div class="tool-config-subtitle">
              {{ currentTool.software_name }}
              <span v-if="currentTool.desc"> · {{ currentTool.desc }}</span>
            </div>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            @click="closeConfig"
            aria-label="Close configuration dialog"
          >
            ✕
          </button>
        </div>

        <!-- Modal body -->
        <div class="tool-config-body">
          <!-- 1️⃣ Valid combination options: directly bind step.payload -->
          <div class="mb-3">
            <div v-if="toolkitSplitDict && currentPayload">
              <!-- Original ConfigSelector template -->
              <div class="config-selector">
                <div
                  v-for="field in toolkitSplitDict.fields"
                  :key="field.key"
                  class="mb-2"
                >
                  <label class="form-label form-label-sm mb-1">
                    {{ field.label }}
                  </label>
                  <select
                    v-model="currentPayload[field.key]"
                    class="form-select form-select-sm"
                  >
                    <option disabled value="">Please select</option>
                    <option
                      v-for="opt in (configOptions[field.key] || [])"
                      :key="opt"
                      :value="opt"
                    >
                      {{ opt }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <div v-else class="bp-card-caption">
              This tool does not have a valid combination dictionary configured yet.
              You can enable it by configuring TOOL_COMBO_CONFIG on the backend.
            </div>
            <div class="bp-card-caption mt-1">
              <span v-if="isSelectableOptionsValid(currentPayload?.selectableOptions)">
                ✅ The current combination is valid
              </span>
              <span v-else>
                ❌ The current combination is invalid. Please adjust the configuration or choose another tool.
              </span>
            </div>
            <div class="config-section-divider my-2" style="border-top: 1px solid #ddd;"></div>
            <br /><br />
          </div>

          <!-- 2️⃣ Step parameter area: bind to step.payload -->
          <div
            v-if="currentPayload"
            class="step-config-panel mb-3"
          >
            <div class="d-flex justify-content-between align-items-center mb-2">
              <label class="form-label form-label-sm mb-0">
                Step Parameters
              </label>
              <span class="bp-card-caption">
                Current step: {{ stepNormalizedPayload.software_name }} {{ stepNormalizedPayload.feature }}
              </span>
            </div>

            <!-- Text params -->
            <div
              v-for="paramName in Object.keys(stepNormalizedPayload.params || {})"
              :key="'step-param-' + paramName"
              class="step-field-row"
            >
              <label class="form-label form-label-sm step-field-label">
                {{ paramName }}
              </label>
              <input
                v-model="stepNormalizedPayload.params[paramName]"
                type="text"
                class="form-control form-control-sm step-field-control"
                :placeholder="'Please enter ' + paramName"
                :title="paramName"
                :disabled="loadingStep"
              />
            </div>

            <!-- Select params: selectableOptions[optionKey][groupKey] -->
            <div
              v-for="(groupMap, optionKey) in stepNormalizedPayload.selectableOptions"
              :key="'select-' + optionKey"
            >
              <div
                v-for="(options, groupKey) in groupMap"
                :key="`${optionKey}-${groupKey}`"
                class="step-field-row"
              >
                <label class="form-label form-label-sm step-field-label">
                  {{ optionKey }} | {{ groupKey }}
                </label>
                <select
                  v-model="stepNormalizedPayload.selected[optionKey][groupKey]"
                  class="form-control form-control-sm step-field-control"
                  :title="`${optionKey}-${groupKey}`"
                  :disabled="loadingStep"
                  @change="updateSelectOption(stepNormalizedPayload, optionKey, groupKey)"
                >
                  <option
                    v-for="option in options"
                    :key="option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Index selection: from selectableIndex -->
            <div
              v-if="stepNormalizedPayload.selectableIndex && Object.keys(stepNormalizedPayload.selectableIndex).length"
              class="mt-2"
            >
              <div class="d-flex justify-content-between align-items-center mb-1">
                <label class="form-label form-label-sm mb-0">
                  Index
                </label>
                <span class="bp-card-caption">
                  From selectableIndex
                </span>
              </div>

              <!-- Special handling for ref: ref_species / ref_release -->
              <template v-if="stepNormalizedPayload.selectableIndex.ref && Object.keys(stepNormalizedPayload.selectableIndex.ref).length">
                <div class="step-field-row">
                  <label class="form-label form-label-sm step-field-label">
                    Reference Species (ref_species)
                  </label>
                  <select
                    v-model="stepNormalizedPayload.selected_index.ref_species"
                    class="form-control form-control-sm step-field-control"
                    :disabled="loadingStep"
                  >
                    <option
                      v-for="species in Object.keys(stepNormalizedPayload.selectableIndex.ref)"
                      :key="species"
                      :value="species"
                    >
                      {{ species }}
                    </option>
                  </select>
                </div>

                <div class="step-field-row">
                  <label class="form-label form-label-sm step-field-label">
                    Reference Release (ref_release)
                  </label>
                  <select
                    v-model="stepNormalizedPayload.selected_index.ref_release"
                    class="form-control form-control-sm step-field-control"
                    :disabled="loadingStep"
                  >
                    <option
                      v-for="release in (stepNormalizedPayload.selectableIndex.ref[stepNormalizedPayload.selected_index.ref_species] || [])"
                      :key="release"
                      :value="release"
                    >
                      {{ release }}
                    </option>
                  </select>
                </div>
              </template>

              <!-- Other index keys: key → value into selected_index -->
              <template
                v-for="(options, idxKey) in stepNormalizedPayload.selectableIndex"
                :key="'index-' + idxKey"
              >
                <div
                  v-if="idxKey !== 'ref'"
                  class="step-field-row"
                >
                  <label class="form-label form-label-sm step-field-label">
                    {{ idxKey }}
                  </label>
                  <select
                    v-model="stepNormalizedPayload.selected_index[idxKey]"
                    class="form-control form-control-sm step-field-control"
                    :disabled="loadingStep"
                  >
                    <option
                      v-for="opt in options"
                      :key="opt"
                      :value="opt"
                    >
                      {{ opt }}
                    </option>
                  </select>
                </div>
              </template>
            </div>
          </div>

          <!-- 3️⃣ Notes -->
          <div class="mb-2" v-if="localSteps[currentIndex]?.payload">
            <label class="form-label form-label-sm mb-1">
              Comment (optional)
            </label>
            <textarea
              v-model="localSteps[currentIndex].payload.comment"
              @input="notesDirty = true"
              rows="2"
              class="form-control form-control-sm"
              placeholder="Comments are shown only in the generated script and do not affect execution"
            ></textarea>
          </div>
          <div class="bp-card-caption">
            Preset comments are automatically filled when switching steps.
            Once you modify them manually, subsequent step changes will not overwrite your edits.
          </div>
        </div>

        <div class="tool-config-footer d-flex justify-content-end gap-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            @click="closeConfig"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-sm btn-primary"
            :disabled="loadingStep || !isSelectableOptionsValid(localSteps[currentIndex].payload.selectableOptions)"
            @click="confirmConfig"
          >
            Confirm & Add to Pipeline
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import API_BASE_URL from '@/api'
import axios from 'axios'

export default {
  name: 'WorkflowComponent',
  props: {
    toolkitMergeList: Array,
    activeCategory: [String, null],
    initialPool: Object,
    steps: { type: Array, required: true },
    stepIndex: { type: Number, required: true },
  },
  emits: ['update:steps','update:stepIndex'],
  data() {
    return {
      keyword: '',
      currentTool: null,
      notesDirty: false,
      showConfig: false,
      toolkitSplitDict: null,

      loadingStep: false,
      lastCoreFields: null,
      localSteps: [],
    };
  },
  created() {
    this.localSteps = JSON.parse(JSON.stringify(this.steps));
  },
  watch: {
    steps: {
      deep: true,
      handler(newVal) {
        this.localSteps = JSON.parse(JSON.stringify(newVal));
      }
    },
    coreFields: {
      handler(newVal) {
        const step = this.localSteps?.[this.currentIndex];
        const payload = step?.payload;
        if (!payload) return;
        const [feature, sequencing_mode, other_feature] = newVal;
        const ready = feature != null || sequencing_mode != null || other_feature != null;
        if (!ready) return;
        const prev = this.lastCoreFields;
        if (!prev) {
          this.lastCoreFields = [...newVal];
          this.handleCoreFieldChange();
          return;
        }
        const same = newVal.length === prev.length &&
          newVal.every((v, i) => v === prev[i]);
        if (same) return;
        this.lastCoreFields = [...newVal];
        this.handleCoreFieldChange();
      },
    },
  },

  computed: {
    currentIndex: {
      get() { return this.stepIndex },
      set(v) { this.$emit('update:stepIndex', v) }
    },
    filteredTools() {
      const kw = (this.keyword || '').trim().toLowerCase();
      return this.toolkitMergeList.filter(tool => {
        if (!tool) return false;
        const matchCategory =
          this.activeCategory == null ||
          (Array.isArray(tool.feature)
            ? tool.feature.includes(this.activeCategory)
            : tool.feature === this.activeCategory);
        if (!kw) return matchCategory;
        const name = (tool.software_name || '').toLowerCase();
        const displayName = (tool.display_name || '').toLowerCase();
        const desc = (tool.desc || '').toLowerCase();
        const matchTag = (tool.tags || []).some(tag =>
          tag.toString().toLowerCase().includes(kw)
        );
        return matchCategory && (
          name.includes(kw) ||
          displayName.includes(kw) ||
          desc.includes(kw) ||
          matchTag
        );
      });
    },
    currentPayload () {
      return this.localSteps?.[this.currentIndex]?.payload || null
    },
    configOptions () {
      if (!this.toolkitSplitDict || !this.currentPayload) return {}
      const result = {}
      const allKeys = (this.toolkitSplitDict.fields || []).map(f => f.key)
      const validConfigs = this.toolkitSplitDict.validConfigs || []
      const payload = this.currentPayload
      allKeys.forEach((key, index) => {
        const filtered = validConfigs.filter(cfg => {
          for (let i = 0; i < index; i++) {
            const prevKey = allKeys[i]
            const val = payload[prevKey]
            if (val && cfg[prevKey] !== val) return false
          }
          return true
        })
        const set = new Set(filtered.map(cfg => cfg[key]))
        result[key] = Array.from(set)
      })
      return result
    },
    stepNormalizedPayload () {
      const mv = this.currentPayload || {}

      // Ensure these fields exist and are objects (mutate mv in-place to keep reference)
      if (!mv.params || typeof mv.params !== 'object') mv.params = {}
      if (!mv.selectableOptions || typeof mv.selectableOptions !== 'object') mv.selectableOptions = {}
      if (!mv.selected || typeof mv.selected !== 'object') mv.selected = {}
      if (!mv.selectableIndex || typeof mv.selectableIndex !== 'object') mv.selectableIndex = {}
      if (!mv.selected_index || typeof mv.selected_index !== 'object') mv.selected_index = {}

      const base = mv

      // selectableOptions → selected default value logic
      Object.entries(base.selectableOptions).forEach(([optionKey, groupMap]) => {
        if (!base.selected[optionKey] || typeof base.selected[optionKey] !== 'object') {
          base.selected[optionKey] = {}
        }
        Object.entries(groupMap || {}).forEach(([groupKey, options]) => {
          if (!(groupKey in base.selected[optionKey])) {
            base.selected[optionKey][groupKey] =
              Array.isArray(options) && options.length > 0 ? options[0] : ''
          }
        })
      })

      // 1) ref: species + release
      const refIndex = base.selectableIndex.ref
      if (refIndex && typeof refIndex === 'object') {
        const speciesKeys = Object.keys(refIndex)
        if (speciesKeys.length) {
          if (
            !base.selected_index.ref_species ||
            !refIndex[base.selected_index.ref_species]
          ) {
            base.selected_index.ref_species = speciesKeys[0]
          }
          const releases = refIndex[base.selected_index.ref_species] || []
          if (
            !base.selected_index.ref_release ||
            !releases.includes(base.selected_index.ref_release)
          ) {
            base.selected_index.ref_release = releases[0] || ''
          }
        }
      }

      // 2) other index keys: key → value into selected_index[key]
      Object.entries(base.selectableIndex).forEach(([idxKey, options]) => {
        if (idxKey === 'ref') return
        if (!Array.isArray(options)) return
        if (!base.selected_index[idxKey]) {
          base.selected_index[idxKey] = options[0] || ''
        }
      })

      return base
    },

    coreFields() {
      const step = this.localSteps[this.currentIndex];
      const payload = step?.payload || {};
      return [
        payload.feature,
        payload.sequencing_mode,
        payload.other_feature,
      ];
    },
  },

  methods: {
    next() { this.currentIndex++ },
    prev() { this.currentIndex-- },
    updateSelectOption(step, optionKey, groupKey) {
      const selectedValue = step.selected[optionKey][groupKey];
      if (optionKey === "fq1") {
        const matchingValue = selectedValue.replace(/_1\.fq\.gz$/, '_2.fq.gz');
        const options = step.selectableOptions.fq2[groupKey] || [];
        if (options.includes(matchingValue)) {
          step.selected.fq2[groupKey] = matchingValue;
        }
      } else if (optionKey === "fq2") {
        const matchingValue = selectedValue.replace(/_2\.fq\.gz$/, '_1.fq.gz');
        const options = step.selectableOptions.fq1[groupKey] || [];
        if (options.includes(matchingValue)) {
          step.selected.fq1[groupKey] = matchingValue;
        }
      }
    },

    async openConfig(tool) {
      this.currentTool = tool;
      this.localSteps[this.currentIndex] = {
        id: this.currentIndex + 1,
        payload: {
          software_name: tool.software_name,
          feature: tool.feature,
          sequencing_mode: tool.sequencing_mode,
          other_feature: tool.other_feature,
          comment: '',
          params: {},
          selectableOptions: {},
          selected: {},
          selectableIndex: {},
          selected_index: {},
        },
      };
      const resp = await axios.post(
        `${API_BASE_URL}/api/batch_code/open_config/`,
        {
          steps: this.localSteps,
          initial_pool: this.initialPool,
        }
      );
      this.localSteps = resp.data.steps;
      this.notesDirty = false;
      this.showConfig = true;
      const res = await axios.post(
        `${API_BASE_URL}/api/common/get_ToolComboConfig/`, { tool }
      );
      this.toolkitSplitDict = res.data.ToolComboConfig
      const p = this.localSteps[this.currentIndex].payload;
      p.comment = (
        this.toolkitSplitDict?.comment || []
      ).find(i =>
        i.feature === p.feature &&
        i.sequencing_mode === p.sequencing_mode &&
        i.other_feature === p.other_feature
      )?.comment || '';
    },
    closeConfig() {
      this.showConfig = false;
      this.currentTool = null;
      this.notesDirty = false;
    },
    isSelectableOptionsValid(options) {
      if (!options || typeof options !== 'object') return false;
      const stack = [options];
      while (stack.length) {
        const cur = stack.pop();
        if (Array.isArray(cur)) {
          if (cur.length === 0) return false;
          continue;
        }
        if (cur && typeof cur === 'object') {
          Object.values(cur).forEach(v => stack.push(v));
        }
      }
      return true;
    },
    confirmConfig() {
      if (!this.localSteps[this.currentIndex]?.payload) return;
      this.currentIndex += 1;
      this.$emit('update:steps', JSON.parse(JSON.stringify(this.localSteps)));
      this.closeConfig();
    },

    async handleCoreFieldChange() {
      if (this.loadingStep) return;

      const step = this.localSteps[this.currentIndex];
      if (!step || !step.payload) return;

      const payload = step.payload;

      // 1. Check whether all three fields are strings
      const coreFieldsAreStrings =
        typeof payload.feature === 'string' &&
        typeof payload.sequencing_mode === 'string' &&
        typeof payload.other_feature === 'string';
      if (!coreFieldsAreStrings) {
        return;
      }

      this.lastCoreFields = [...this.coreFields];

      try {
        this.loadingStep = true;

        this.localSteps[this.currentIndex] = {
          id: this.currentIndex + 1,
          payload: {
            software_name: payload.software_name,
            feature: payload.feature,
            sequencing_mode: payload.sequencing_mode,
            other_feature: payload.other_feature,
            comment: payload.comment,
            params: {},
            selectableOptions: {},
            selected: {},
            selectableIndex: {},
            selected_index: {},
          },
        };

        const resp = await axios.post(
          `${API_BASE_URL}/api/batch_code/confirm_steps/`,
          {
            steps: this.localSteps,
            initial_pool: this.initialPool,
          }
        );

        this.localSteps = resp.data.steps;

        if (!this.notesDirty && this.toolkitSplitDict) {
          const p = this.localSteps[this.currentIndex].payload;
          p.comment = (
            this.toolkitSplitDict?.comment || []
          ).find(i =>
            i.feature === p.feature &&
            i.sequencing_mode === p.sequencing_mode &&
            i.other_feature === p.other_feature
          )?.comment || '';
        }
      } finally {
        this.loadingStep = false;
      }
    },
  },
};
</script>

<style scoped>
.bp-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
}

.bp-card-header {
  padding: 12px 16px 10px;
  border-bottom: 1px solid #f3f4f6;
}

.bp-card-title-sm {
  font-size: 0.85rem;
  font-weight: 600;
}

.bp-card-caption {
  font-size: 0.8rem;
  color: #9ca3af;
}

/* Main area */
.store-grid-wrapper {
  position: relative;
  padding: 10px 12px 14px;
  max-height: 460px;
  overflow: auto;
}

.store-grid-disabled {
  filter: grayscale(0.2);
}

/* Search input */
.store-search-input {
  font-size: 0.85rem;
}

/* Tool card */
.tool-card {
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.tool-card--disabled {
  opacity: 0.7;
}

.tool-card-body {
  margin-bottom: 6px;
  cursor: pointer;
}

.tool-card-body:hover {
  background: #f3f4f6;
  border-radius: 10px;
  margin: -4px -4px 2px;
  padding: 4px 4px 2px;
}

.tool-card-title-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 6px;
}

.tool-card-title {
  font-size: 0.92rem;
  font-weight: 500;
}

.tool-card-chip {
  font-size: 0.72rem;
  padding: 2px 6px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4b5563;
  white-space: nowrap;
}

.tool-card-desc {
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 2px;
}

.tool-card-tags {
  margin-top: 2px;
}

.tool-tag {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 999px;
  background: #e5e7eb;
  font-size: 0.72rem;
  color: #4b5563;
  margin-right: 4px;
}

.tool-card-footer {
  display: flex;
  justify-content: flex-end;
}

/* Disabled overlay */
.store-disabled-overlay {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle at center,
    rgba(15, 23, 42, 0.65),
    rgba(15, 23, 42, 0.85)
  );
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.store-disabled-hint {
  max-width: 260px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.9);
  color: #e5e7eb;
  font-size: 0.78rem;
  text-align: center;
}

/* Empty state */
.bp-empty {
  padding: 24px;
  text-align: center;
  border-radius: 12px;
  background: #f9fafb;
  border: 1px dashed #e5e7eb;
}

/* Modal styles */
.tool-config-backdrop {
  position: fixed;
  inset: 0;
  z-index: 999;
  background: rgba(15, 23, 42, 0.55);
}

.tool-config-modal {
  width: 100%;
  max-width: 480px;
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.35);
  display: flex;
  flex-direction: column;
}

.tool-config-header {
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
}

.tool-config-title {
  font-size: 1rem;
  font-weight: 600;
}

.tool-config-subtitle {
  font-size: 0.8rem;
  color: #6b7280;
}

.tool-config-body {
  padding: 10px 16px 8px;
  font-size: 0.85rem;
}

.tool-config-footer {
  padding: 8px 16px 10px;
  border-top: 1px solid #f3f4f6;
}

/* Divider */
.config-section-divider {
  border-top: 1px dashed #e5e7eb;
  margin: 6px 0 10px;
}

/* Scrollbar */
.store-grid-wrapper::-webkit-scrollbar {
  width: 6px;
}
.store-grid-wrapper::-webkit-scrollbar-thumb {
  background-color: rgba(148, 163, 184, 0.8);
  border-radius: 999px;
}

/* Small labels */
.form-label-sm {
  font-size: 0.78rem;
  color: #6b7280;
}

.step-config-panel {
  padding-top: 4px;
}
.step-field-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.step-field-label {
  min-width: 140px;
  margin-bottom: 0;
  color: #6b7280;
}
.step-field-control {
  flex: 1;
}
.config-selector {
  /* Add extra styles here if needed */
}
</style>

